import { useState, useEffect } from "react";
import { Modal, Table, InputNumber, Input } from "antd";
import dayjs from "dayjs";
import { withLocale } from "../../l10n";
import { STATE_ACTIVE, STATE_CANCELLED } from "../../utils/constants";


const MIN_MONTHS = 1;
const MAX_MONTHS = 120;

/**
 * Obtiene la fecha base para calcular la nueva fecha de renovación.
 */
const getBaseDate = (row, subscriptionType) => {
  const nextRenewal = dayjs(row.renewal_date).add(row.renewal_interval, 'month');

  return (subscriptionType === 'm2m' && !nextRenewal.isAfter(dayjs()))
    ? dayjs()
    : nextRenewal;
};

/**
 * Calcula los meses necesarios para que la renovación de una fila alcance una fecha futura.
 */
const monthsNeeded = (row, subscriptionType) =>
  Math.max(MIN_MONTHS, dayjs().diff(getBaseDate(row, subscriptionType), 'month') + 1);


/**
 * Modal para renovar suscripciones.
 */
function RenewSubscriptionsModal({
  open,
  loading,
  rows,
  onSubmit,
  onCancel,
  getRowKey,
  lang
}) {

  const [renewalMonths, setRenewalMonths] = useState(3);
  const [orderId, setOrderId] = useState('');


  useEffect(() => {
    if (open) {
      setRenewalMonths(3);
      setOrderId('');
    }
  }, [open]);

  const subscriptionType = rows[0]?.subscription_type;

  const isValidMonths = Number.isInteger(renewalMonths)
    && renewalMonths >= MIN_MONTHS
    && renewalMonths <= MAX_MONTHS;

  const isValidOrder = orderId.trim() !== '';

  /**
   * Numero de meses que necesita la fila más atrasada para que su renovación alcance una fecha futura.
   */
  const requiredMonths = rows.reduce(
    (max, row) => Math.max(max, monthsNeeded(row, subscriptionType)),
    MIN_MONTHS
  );

  const isShort = isValidMonths && renewalMonths < requiredMonths;
  const canSubmit = isValidMonths && isValidOrder && !isShort;

  const title = rows.length === 1
    ? `${lang.handle('Renovando')} 1 ${lang.handle('suscripción')}`
    : `${lang.handle('Renovando')} ${rows.length} ${lang.handle('suscripciones')}`;

  /**
   * Maneja el envío del formulario de renovación.
   */
  const handleSubmit = () => {
    if (!canSubmit) return;

    const stateField = subscriptionType === 'm2m' ? 'state' : 'ki_subscription_state';

    const renewalData = rows.map(row => {
      const data = {
        uuid: row.uuid,
        renewal_date: getBaseDate(row, subscriptionType).format('DD-MM-YYYY'),
        renewal_interval: renewalMonths,
      };

      if (row.subscription_state === STATE_CANCELLED) {
        data[stateField] = STATE_ACTIVE;
      }

      return data;
    });

    onSubmit(renewalData, subscriptionType, orderId.trim());
  };

  /**
   * Define las columnas de la tabla de suscripciones.
   */
  const columns = [
    {
      title: 'UUID',
      dataIndex: 'uuid',
      width: '50%',
      render: (uuid) => (
        <span className="uuid-cell">{uuid}</span>
      )
    },
    {
      title: lang.handle('Próxima renovación'),
      dataIndex: 'date_to_renew',
      width: '25%',
    },
    {
      title: lang.handle('Nueva fecha'),
      width: '25%',
      render: (_, record) => {
        if (!isValidMonths) {
          return <span className="new-date-cell">-</span>;
        }

        const newDate = getBaseDate(record, subscriptionType).add(renewalMonths, 'month');
        const isRowShort = renewalMonths < monthsNeeded(record, subscriptionType);

        return (
          <span className={`new-date-cell ${isRowShort ? 'new-date-cell-invalid' : ''}`}>
            {newDate.format('DD-MM-YYYY')}
          </span>
        );
      }
    }
  ];

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      cancelText={lang.handle('Cerrar')}
      okText={lang.handle('Renovar')}
      confirmLoading={loading}
      maskClosable={false}
      okButtonProps={{ disabled: !canSubmit }}
    >
      <div className="renewal-form-container">
        <div className="renewal-field">
          <label className="renewal-field-label">{lang.handle('Pedido')}:</label>
          <Input
            className="renewal-order-input"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="20PED4596"
            status={!isValidOrder ? 'error' : ''}
            maxLength={50}
          />
        </div>
        <div className="renewal-field">
          <label className="renewal-field-label">{lang.handle('Meses a renovar')}:</label>
          <InputNumber
            className="renewal-months-input"
            value={renewalMonths}
            onChange={(value) => setRenewalMonths(value)}
            min={MIN_MONTHS}
            max={MAX_MONTHS}
            precision={0}
            status={isShort? 'error' : ''}
          />
            {isShort && (
            <span className="renewal-field-error">
              {`${lang.handle('Se deben renovar al menos')} ${requiredMonths} ${lang.handle('meses')}`}
            </span>
          )}
        </div>
      </div>
      <div className="renewal-preview">
        <Table
          size="small"
          pagination={false}
          dataSource={rows}
          rowKey={getRowKey}
          rowClassName={(record) =>
            renewalMonths < monthsNeeded(record, subscriptionType) ? 'row-insufficient' : ''
          }
          columns={columns}
        />
      </div>
    </Modal>
  );
}

export default withLocale(RenewSubscriptionsModal);
